/** SPA-local client for chat file attachments. Posts to the chats worker's OWN flow-dispatch path (`chat-upload`):
 *  embedded, the middleware forwards the prefix; standalone, it resolves same-origin. A route failure stays soft. */

import { getErrorMessage } from '@federa/utils/errors/error-core';
import { fileToBase64 } from '@federa/utils/encoding/base64';
import { safeParseResponseJson } from '@federa/utils/http/parse-json-body';
import { buildBearerJsonPostInit } from '@federa/const/http-request-init';
import { asPlainObjectOrUndefined } from '@federa/const/record-guards';
import { ERROR_MESSAGES } from '@federa/const/error-messages';
import { CHATS_MFE_API_BASE } from '@federa/const/chats-viewer-routes';
import { API_TIMEOUTS } from '@federa/const/timing';
import { fetchTracedEdge } from '@federa/components/lib/api/traced-edge-fetch';
import { extractStringInput } from '@federa/utils/validation/extract-string-field';
import { resolveWsCredential } from './ws-credential.ts';
import type { FlowName } from '@federa/types/flows-flow-name.generated';
import type { UploadChatFileResult } from '@federa/types/conversations-chat-structs';

interface UploadChatFileParams {
	file: File;
	userId: string;
	conversationId?: string;
	onProgress?: (progress: number) => void;
}

/** Failure-shape constructor: reads the optional message/error fields off the parsed wire body (or undefined when the body never parsed). */
function buildUploadFailure(body: Record<string, unknown> | undefined): UploadChatFileResult {
	return {
		success: false,
		message: extractStringInput(body, 'message', ERROR_MESSAGES.REQUEST_FAILED),
		...(typeof body?.error === 'string' ? { error: body.error } : {}),
	};
}

export async function uploadChatFile(params: UploadChatFileParams): Promise<UploadChatFileResult> {
	try {
		const { file, userId, conversationId, onProgress } = params;
		const progress = onProgress ?? (() => {});

		progress(10);
		const base64 = await fileToBase64(file);
		progress(30);

		// The chats flow-dispatch mount verifies the caller and binds the subject from the credential, so an upload that
		// carries none is 401'd -- the same gate `function-details` and the function cards answer to. Resolved per call:
		// a guest has no token until the bootstrap create returns one, and a Firebase token expires in ~1h.
		const credential = await resolveWsCredential(userId, undefined);
		// `fetchTracedEdge`, so the upload carries the originating-edge `traceparent` and the worker's `chat-upload` run
		// joins the interaction that started it. LONG-RUNNING, not the standard 10s: the body is the whole file
		// base64-encoded, so a large attachment on a slow uplink is a legitimate slow request rather than a hung one.
		const response = await fetchTracedEdge(`${CHATS_MFE_API_BASE}/${'chat-upload' satisfies FlowName}`, {
			...buildBearerJsonPostInit(
				{
					file: base64,
					file_name: file.name,
					file_type: file.type,
					user_id: userId,
					conversation_id: conversationId ?? null,
				},
				credential,
			),
			timeout: API_TIMEOUTS.longRunning,
		});
		progress(80);

		const parseResult = await safeParseResponseJson(response);
		progress(100);

		// The wire body is the canonical flow-dispatch envelope, NOT UploadChatFileResult -- read it field-wise off the parsed record so the caller-facing result is CONSTRUCTED here rather than cast into.
		const body = parseResult.ok ? asPlainObjectOrUndefined(parseResult.data) : undefined;

		if (!response.ok || !body) return buildUploadFailure(body);
		return { success: true, data: asPlainObjectOrUndefined(body.data) };
	} catch (error) {
		return {
			success: false,
			message: 'Failed to upload file',
			error: getErrorMessage(error),
		};
	}
}
